import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { initialState, MemberUserProfile } from "./auth-state.model";
import { User } from "@auth0/auth0-angular";
import {
  MembersByUser,
  MembersByUserResponse,
  MembersV2Service,
  UpdateMemberInformationApiV2MembersOrgPatch$Params,
} from "@rr/shared-data-access-api";
import { ThemeConfig } from "../../models";
import { computed, inject } from "@angular/core";
import { StorageService } from "../../services/storage.service";
import { DEFAULT_THEME_LOADER, STORAGE_KEYS } from "../../constants";
import { first } from "lodash-es";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { catchError, exhaustMap, of, pipe, tap } from "rxjs";
import { tapResponse } from "@ngrx/operators";
import { MessageService } from "primeng/api";
import { UtilThemeHelper } from "@rr/shared-util";

export const AuthStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed((store) => ({
    tenant: computed(() => store.user()?.["org_name"] ?? null),
    themeHeaderLogo: computed(() => store.theme()?.headerLogo ?? "assets/images/logo/agridence.svg"),
    primaryOrganization: computed(() => store.memberUser()?.primaryOrganization ?? null),
    isAdminRole: computed(() => store.memberUser()?.primaryOrganization?.role?.toLowerCase() === "rr/admin"),
    isEditorRole: computed(() => store.memberUser()?.primaryOrganization?.role?.toLowerCase() === "rr/editor"),
    themeLoader: computed(() => ({
      logo: store.theme()?.fullLogo ?? null,
      primary: store.theme()?.style?.colors?.primaryColor ?? null,
      secondary: store.theme()?.style?.colors?.secondaryColor ?? null,
    })),
  })),
  withComputed((store) => ({
    isGpsnr: computed(() => store.tenant()?.toLowerCase() === "gpsnr"),
  })),
  withMethods(
    (
      store,
      storageService = inject(StorageService),
      membersService = inject(MembersV2Service),
      messageService = inject(MessageService),
    ) => ({
      updatePrimaryOrganization: rxMethod<UpdateMemberInformationApiV2MembersOrgPatch$Params>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          exhaustMap((request) => {
            return membersService.updateMemberInformationApiV2MembersOrgPatch(request).pipe(
              catchError(() => of(null)),
              tapResponse({
                next: (response) => {
                  if (response) {
                    patchState(store, (state) => ({
                      memberUser: {
                        ...(state.memberUser ?? ({} as MemberUserProfile)),
                        organizations: (state.memberUser ?? ({} as MemberUserProfile))?.organizations.map(
                          (org: MembersByUser) => {
                            if (org.orgId === store.primaryOrganization()?.orgId) {
                              return {
                                ...org,
                                companyName: response.name,
                                contactEmail: response.contactEmail,
                              };
                            }

                            return org;
                          },
                        ),
                        primaryOrganization: {
                          ...(state.memberUser?.primaryOrganization ?? ({} as MembersByUser)),
                          companyName: response.name,
                          contactEmail: response.contactEmail,
                        },
                      },
                    }));
                    messageService.add({
                      severity: "success",
                      detail: "Success! Company information has been updated.",
                    });
                  } else {
                    messageService.add({
                      severity: "error",
                      detail: "Failed to update company information.",
                    });
                  }
                },
                error: (error) => console.error(error),
                complete: () => patchState(store, { isLoading: false }),
              }),
            );
          }),
        ),
      ),
      loadMemberByUser: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          exhaustMap(() => {
            return membersService.getListOfMembersApiV2MembersUserMeGet().pipe(
              catchError(() => of(null)),
              tapResponse({
                next: (membersByUser: MembersByUserResponse | null) => {
                  const selectedPrimaryOrg = storageService.get(STORAGE_KEYS.selected_primary_org) ?? null;
                  let memberPrimaryOrg = null;

                  if (selectedPrimaryOrg) {
                    memberPrimaryOrg =
                      (membersByUser?.organizations ?? []).length > 0
                        ? (membersByUser?.organizations.find(
                            (org) => org.orgId?.toLowerCase() === (selectedPrimaryOrg as string)?.toLowerCase(),
                          ) ?? null)
                        : null;
                  } else {
                    memberPrimaryOrg =
                      (membersByUser?.organizations ?? []).length > 0
                        ? (first(membersByUser?.organizations) ?? null)
                        : null;
                  }

                  if (memberPrimaryOrg?.orgId) {
                    storageService.set(STORAGE_KEYS.selected_primary_org, memberPrimaryOrg.orgId);
                  }

                  patchState(store, (state) => ({
                    memberUser: {
                      ...(state.memberUser ?? ({} as MemberUserProfile)),
                      ...membersByUser,
                      primaryOrganization: memberPrimaryOrg,
                    },
                  }));
                },
                error: (error) => console.error(error),
                complete: () => patchState(store, { isLoading: false }),
              }),
            );
          }),
        ),
      ),
      setToken(token: string | null): void {
        patchState(store, { token });
      },
      setUser(user: User | null): void {
        patchState(store, { user });
      },
      setCallback(callback: (() => void) | null): void {
        patchState(store, { callback });
      },
      switchPrimaryOrg(selectedPrimaryOrg?: MembersByUser | null) {
        const primaryOrganization = selectedPrimaryOrg ?? store.pendingSwitchOrg();

        if (!primaryOrganization) {
          patchState(store, { pendingSwitchOrg: null });
          return;
        }

        storageService.set(STORAGE_KEYS.selected_primary_org, primaryOrganization?.orgId);
        patchState(store, (state) => ({
          memberUser: { ...(state.memberUser as MemberUserProfile), primaryOrganization },
          pendingSwitchOrg: null,
        }));
      },
      setPendingSwitchOrg(pendingSwitchOrg?: MembersByUser | null) {
        patchState(store, { pendingSwitchOrg: pendingSwitchOrg ?? null });
      },
      setTheme(theme: ThemeConfig | null): void {
        if (store.tenant()) {
          if (theme) {
            const isLightMode = UtilThemeHelper.isLightMode(theme?.style?.colors?.bodyBackground ?? "#ffffff");
            theme.mode = isLightMode ? "rr-theme" : "rr-theme-dark";
          }
          patchState(store, { theme });
        }
        const themeLoader = {
          logo: theme?.fullLogo ?? DEFAULT_THEME_LOADER.logo,
          primary: theme?.style?.colors?.primaryColor ?? DEFAULT_THEME_LOADER.primary,
          secondary: theme?.style?.colors?.secondaryColor ?? DEFAULT_THEME_LOADER.secondary,
        };
        storageService.set(store.tenant() ? STORAGE_KEYS.tld : STORAGE_KEYS.default_tld, themeLoader);
      },
    }),
  ),
);
